import { Check, ChevronDown, Plus } from "lucide-react";
import { useId } from "react";

import { Button } from "~/components/ui/button";
import { Card } from "~/components/ui/card";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "~/components/ui/collapsible";
import { useDecryptedResource } from "~/lib/crypto";
import type { GetKeyGrantFn, KeyManager } from "~/lib/crypto";

import type { GoalGroups, GoalItem, LifeCategory } from "./goal-groups";
import { GoalRow } from "./goal-row";

export type RowProps = {
  onOpen: (item: GoalItem) => void;
  keyManager: KeyManager | null;
  getKeyGrant?: GetKeyGrantFn;
  showPublicLink?: boolean;
};

export function goalCount(count: number): string {
  return `${count} ${count === 1 ? "goal" : "goals"}`;
}

/** Rows of the same kind in one card, divided by hairlines. */
export function GoalRows({
  goals,
  ...rowProps
}: RowProps & { goals: GoalItem[] }) {
  return (
    <Card className="gap-0 divide-y divide-gray-100 overflow-hidden p-0">
      {goals.map((item) => (
        <GoalRow key={item.goal.id} item={item} {...rowProps} />
      ))}
    </Card>
  );
}

/** A category's name is encrypted, so its heading decrypts it. */
export function useCategoryName({
  category,
  keyManager,
  getKeyGrant,
  hideName = false,
}: {
  category: Pick<LifeCategory, "id" | "encryptedName">;
  keyManager: KeyManager | null;
  getKeyGrant?: GetKeyGrantFn;
  hideName?: boolean;
}): { name: string | null; isDecrypting: boolean } {
  const { data, isDecrypting } = useDecryptedResource({
    resourceType: "categoryOfLife",
    resourceId: category.id,
    keyManager,
    getKeyGrant,
    fields: { name: { encrypted: hideName ? null : category.encryptedName } },
  });
  return { name: hideName ? null : (data.name ?? "Untitled"), isDecrypting };
}

function CategoryGroup({
  category,
  goals,
  hideName,
  onAddGoal,
  ...rowProps
}: RowProps & {
  category: LifeCategory;
  goals: GoalItem[];
  hideName: boolean;
  onAddGoal?: (categoryId: string) => void;
}) {
  const { name, isDecrypting } = useCategoryName({
    category,
    keyManager: rowProps.keyManager,
    getKeyGrant: rowProps.getKeyGrant,
    hideName,
  });
  const headingId = useId();

  return (
    <section className="space-y-3" aria-labelledby={headingId}>
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1">
        <h3
          id={headingId}
          className="flex min-w-0 items-baseline gap-2 text-base font-medium break-words text-gray-900"
        >
          {isDecrypting ? (
            <span className="inline-block h-5 w-32 animate-pulse rounded bg-gray-100" />
          ) : (
            name
          )}
          <span className="text-text-tertiary shrink-0 text-sm font-normal">
            {goalCount(goals.length)}
          </span>
        </h3>
        {onAddGoal && (
          <Button
            variant="ghost"
            size="sm"
            className="text-purple-700"
            // The category name stays out of the button's text, which the
            // Meta pixel reports on every click.
            aria-describedby={headingId}
            onClick={() => onAddGoal(category.id)}
          >
            <Plus aria-hidden />
            Add goal
          </Button>
        )}
      </div>
      <GoalRows goals={goals} {...rowProps} />
    </section>
  );
}

/**
 * Open goals under their category, then the finished goals in a "Done"
 * section that starts closed. The client and their coach see the same list;
 * only the client can add a goal.
 */
export function GoalList({
  groups,
  hideCategoryNames = false,
  onAddGoal,
  ...rowProps
}: RowProps & {
  groups: GoalGroups;
  /** For a coach the client shares goals with but not the vision, which holds the category names. */
  hideCategoryNames?: boolean;
  onAddGoal?: (categoryId: string) => void;
}) {
  return (
    <div className="space-y-6">
      {groups.categories.map(({ category, goals }) => (
        <CategoryGroup
          key={category.id}
          category={category}
          goals={goals}
          hideName={hideCategoryNames}
          onAddGoal={onAddGoal}
          {...rowProps}
        />
      ))}
      <DoneGoals goals={groups.done} {...rowProps} />
    </div>
  );
}

/** Finished goals in a "Done" section that starts closed; nothing when there are none. */
export function DoneGoals({
  goals,
  ...rowProps
}: RowProps & { goals: GoalItem[] }) {
  if (goals.length === 0) return null;
  return (
    <Collapsible className="group/done space-y-3">
      <CollapsibleTrigger asChild>
        <Button variant="default" className="w-full justify-between">
          <span className="flex items-center gap-2">
            <Check className="text-green-700" aria-hidden />
            Done · {goalCount(goals.length)}
          </span>
          <ChevronDown
            className="transition-transform group-data-[state=open]/done:rotate-180"
            aria-hidden
          />
        </Button>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <GoalRows goals={goals} {...rowProps} />
      </CollapsibleContent>
    </Collapsible>
  );
}
